import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import type { NoteTeamAccess } from '@sharedlib/dtos/dto/notes-app/note-collaboration.dto';
import { Subject } from 'rxjs';

export interface NoteAccessOwner {
  readonly name: string;
  readonly email: string;
  readonly avatarUrl?: string;
}

export interface NoteAccessView {
  readonly owner: NoteAccessOwner | null;
  readonly access: NoteTeamAccess | null;
  readonly state: 'loading' | 'ready' | 'legacy' | 'error';
  readonly editable: boolean;
  readonly pending: boolean;
  readonly error: string | null;
}

@Component({
  selector: 'app-note-access-panel',
  imports: [Button2Component, IconComponent, PopoverDirective],
  templateUrl: './note-access-panel.component.html',
  styleUrl: './note-access-panel.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoteAccessPanelComponent {
  private static nextId = 0;
  public readonly view = input.required<NoteAccessView>();
  public readonly teamName = input.required<string>();
  public readonly accessChange = output<NoteTeamAccess>();
  public readonly retry = output<void>();
  public readonly close = output<void>();
  public readonly focusOnOpen = input(true);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');
  private readonly valueButton = viewChild<Button2Component>('valueButton');
  protected readonly optionsId = `note-access-options-${++NoteAccessPanelComponent.nextId}`;
  protected readonly reasonId = `note-access-reason-${NoteAccessPanelComponent.nextId}`;
  protected readonly reason = 'Only the owner, author or team admin can manage this item.';
  protected readonly optionsOpen = signal(false);
  protected readonly closeOptions = new Subject<void>();
  protected readonly canChange = computed(
    () => this.view().state === 'ready' && this.view().editable && !this.view().pending,
  );
  /** The prototype's reason, shown for a control that is off because of WHO is looking, not because a save is under way. */
  protected readonly showReason = computed(() => this.view().state === 'ready' && !this.view().editable && !this.view().pending);
  protected readonly accessLabel = computed(() => (this.view().access === 'read_only' ? 'Read only' : 'Editor'));
  protected readonly ownerInitials = computed(() => this.initials(this.view().owner?.name ?? '?'));
  protected readonly teamInitials = computed(() => this.initials(this.teamName()));

  public constructor() {
    inject(DestroyRef).onDestroy(() => this.closeOptions.complete());
    afterNextRender(() => {
      if (!this.focusOnOpen()) return;
      if (this.canChange()) this.valueButton()?.focus();
      else this.panel()?.nativeElement.focus();
    });
  }

  protected choose(access: NoteTeamAccess): void {
    if (!this.canChange()) return;
    this.closeOptions.next();
    this.valueButton()?.focus();
    if (access !== this.view().access) this.accessChange.emit(access);
  }

  protected optionsVisible(visible: boolean): void {
    this.optionsOpen.set(visible);
    if (!visible) return;
    afterNextRender(
      () =>
        this.document
          .getElementById(this.optionsId)
          ?.querySelector<HTMLButtonElement>('[aria-checked="true"]:not(:disabled)')
          ?.focus(),
      { injector: this.injector },
    );
  }

  protected closePanel(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.close.emit();
  }

  protected optionsKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeOptions.next();
      this.valueButton()?.focus();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const menu = this.document.getElementById(this.optionsId);
    const choices = Array.from(menu?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
    if (!choices.length) return;
    event.preventDefault();
    const current = choices.findIndex((choice) => choice === this.document.activeElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? choices.length - 1
          : (current + (event.key === 'ArrowUp' ? -1 : 1) + choices.length) % choices.length;
    choices[next]?.focus();
  }

  private initials(name: string): string {
    return name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.slice(0, 1).toLocaleUpperCase())
      .join('');
  }
}
